import axios from 'axios';
import {
    ArrowRight,
    CheckCircle2,
    Clock,
    Copy,
    FileText,
    Globe,
    HelpCircle,
    Loader2,
    Mail,
    MapPin,
    Package,
    Phone,
    Plane,
    RefreshCw,
    Search,
    Share2,
    Truck,
    User,
    Weight,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../hooks/useToast';
import {
  buildRecentAwbList,
  clearRecentAwbs,
  loadRecentAwbs,
  normalizeAwb,
  saveRecentAwbs,
} from '../utils/recentAwbs';

// =============================================================================
// TYPES
// =============================================================================

interface TrackingEvent {
  eventDate: Date;
  eventTime: string;
  eventDescription: string;
  eventLocation: string;
  eventType: 'MANUAL' | 'API';
  eventState: string;
  createdDate: Date;
  remarks?: string;
}

interface TrackingData {
  awb: {
    awbNumber: string;
    currentStatus: string;
    bookingDate: Date;
    shipmentValue: number;
    currency: string;
    shipper: {
      personName?: string;
      company?: string;
      city: string;
      country: string;
      phoneNumber: string;
      email?: string;
      address1?: string;
      address2?: string;
      postalCode?: string;
    };
    consignee: {
      personName?: string;
      company?: string;
      city: string;
      country: string;
      phoneNumber: string;
      email?: string;
      address1?: string;
      address2?: string;
      postalCode?: string;
    };
    service: {
      name: string;
      serviceType: string;
    };
    destination: {
      name: string;
      code: string;
    };
    origin?: {
      name: string;
      code: string;
    };
    totalPieces: number;
    totalChargeableWeight: number;
    totalActualWeight?: number;
    totalVolumetricWeight?: number;
  };
  trackingEvents: TrackingEvent[];
  currentStatus: string;
  estimatedDelivery?: Date;
}

// =============================================================================
// STATUS CONFIGURATION
// =============================================================================

const statusConfig: {
  [key: string]: {
    color: string;
    bgColor: string;
    borderColor: string;
    icon: React.ReactNode;
    step: number;
    label: string;
  };
} = {
  DRAFT: {
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    borderColor: 'border-gray-300 dark:border-gray-600',
    icon: <FileText className="h-5 w-5" />,
    step: 0,
    label: 'Draft',
  },
  BOOKED: {
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-300 dark:border-blue-700',
    icon: <CheckCircle2 className="h-5 w-5" />,
    step: 1,
    label: 'Booked',
  },
  PICKED_UP: {
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/30',
    borderColor: 'border-amber-300 dark:border-amber-700',
    icon: <Package className="h-5 w-5" />,
    step: 2,
    label: 'Picked Up',
  },
  IN_TRANSIT: {
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30',
    borderColor: 'border-orange-300 dark:border-orange-700',
    icon: <Truck className="h-5 w-5" />,
    step: 3,
    label: 'In Transit',
  },
  OUT_FOR_DELIVERY: {
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/30',
    borderColor: 'border-purple-300 dark:border-purple-700',
    icon: <MapPin className="h-5 w-5" />,
    step: 4,
    label: 'Out for Delivery',
  },
  DELIVERED: {
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
    borderColor: 'border-emerald-300 dark:border-emerald-700',
    icon: <CheckCircle2 className="h-5 w-5" />,
    step: 5,
    label: 'Delivered',
  },
  RETURNED: {
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/30',
    borderColor: 'border-red-300 dark:border-red-700',
    icon: <ArrowRight className="h-5 w-5 rotate-180" />,
    step: -1,
    label: 'Returned',
  },
  CANCELLED: {
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    borderColor: 'border-gray-300 dark:border-gray-600',
    icon: <FileText className="h-5 w-5" />,
    step: -1,
    label: 'Cancelled',
  },
};

const progressSteps = [
  { key: 'BOOKED', label: 'Booked', icon: <CheckCircle2 className="h-4 w-4" /> },
  { key: 'PICKED_UP', label: 'Picked Up', icon: <Package className="h-4 w-4" /> },
  { key: 'IN_TRANSIT', label: 'In Transit', icon: <Plane className="h-4 w-4" /> },
  {
    key: 'OUT_FOR_DELIVERY',
    label: 'Out for Delivery',
    icon: <Truck className="h-4 w-4" />,
  },
  { key: 'DELIVERED', label: 'Delivered', icon: <CheckCircle2 className="h-4 w-4" /> },
];

const TRACKING_HISTORY_KEY = 'trackingRecentAwbs';
const MAX_RECENT_AWBS = 6;

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const TrackingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasSearchedFromUrl, setHasSearchedFromUrl] = useState(false);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);
  const [recentAwbs, setRecentAwbs] = useState<string[]>(() =>
    loadRecentAwbs(TRACKING_HISTORY_KEY, MAX_RECENT_AWBS)
  );
  const { toast } = useToast();

  // ---------------------------------------------------------------------------
  // SEARCH HANDLER
  // ---------------------------------------------------------------------------

  const handleSearch = async (awbNumber?: string) => {
    const searchTerm = awbNumber || searchQuery;
    const normalizedTerm = normalizeAwb(searchTerm);

    if (!normalizedTerm) {
      toast({
        title: 'Search Required',
        description: 'Please enter an AWB number',
        variant: 'destructive',
      });
      return;
    }

    setHasSearched(true);
    setLoading(true);
    setError(null);
    if (normalizedTerm !== searchQuery) {
      setSearchQuery(normalizedTerm);
    }
    if (searchParams.get('awb') !== normalizedTerm) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('awb', normalizedTerm);
      setSearchParams(nextParams, { replace: true });
    }

    try {
      const response = await axios.get(
        `/api/tracking/customer?awbNumber=${encodeURIComponent(normalizedTerm)}`
      );

      if (response.data.success) {
        setTrackingData(response.data.data);
        setRecentAwbs((prev) => {
          const next = buildRecentAwbList(prev, normalizedTerm, MAX_RECENT_AWBS);
          saveRecentAwbs(TRACKING_HISTORY_KEY, next);
          return next;
        });
        if (!awbNumber) {
          toast({
            title: 'Shipment Found',
            description: `AWB ${response.data.data.awb.awbNumber} - ${response.data.data.currentStatus}`,
          });
        }
      } else {
        setError(response.data.error || 'Shipment not found');
        setTrackingData(null);
      }
    } catch (err) {
      console.error('Error searching shipment:', err);
      setError('Failed to search shipment. Please try again.');
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-search from URL parameters on initial load
  useEffect(() => {
    if (hasSearchedFromUrl || hasSearched) return;

    const awbFromUrl = searchParams.get('awb');
    if (awbFromUrl) {
      const normalized = normalizeAwb(awbFromUrl);
      setSearchQuery(normalized);
      setHasSearchedFromUrl(true);
      handleSearch(normalized);
    }
  }, [searchParams, hasSearchedFromUrl, hasSearched]);

  // ---------------------------------------------------------------------------
  // HELPER FUNCTIONS
  // ---------------------------------------------------------------------------

  const getStatusConfig = (status: string) => {
    return statusConfig[status] || statusConfig['DRAFT'];
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: Date | string, time?: string) => {
    const eventDate = new Date(date);
    const dateStr = eventDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (time) {
      return `${dateStr} at ${time}`;
    }

    return `${dateStr} at ${eventDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const currentStep = useMemo(() => {
    if (!trackingData) return 0;
    const config = getStatusConfig(trackingData.currentStatus);
    return config.step;
  }, [trackingData?.currentStatus]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard`,
    });
  };

  const shareTracking = () => {
    const url = `${window.location.origin}/track?awb=${trackingData?.awb.awbNumber}`;
    navigator.clipboard.writeText(url);
    toast({
      title: 'Link Copied!',
      description: 'Tracking link copied to clipboard',
    });
  };

  // Filter out internal AWB Updated events and sort by date
  const filteredEvents = useMemo(() => {
    if (!trackingData?.trackingEvents) return [];
    return [...trackingData.trackingEvents]
      .filter(
        (event) => !event.eventDescription.toLowerCase().includes('awb updated')
      )
      .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
  }, [trackingData?.trackingEvents]);

  // ---------------------------------------------------------------------------
  // RENDER: SEARCH SECTION
  // ---------------------------------------------------------------------------

  const renderSearchSection = () => (
    <div className="relative">
      {/* Hero Background */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 dark:from-indigo-800 dark:via-purple-800 dark:to-pink-700">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-4 bg-white/20 rounded-2xl backdrop-blur-sm mb-6 ring-1 ring-white/30">
              <Package className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Track Your Shipment
            </h1>
            <p className="text-indigo-100 text-lg sm:text-xl max-w-2xl mx-auto mb-8">
              Enter your AWB number to get real-time updates on your package
            </p>
          </div>
        </div>
      </div>

      {/* Floating Search Card */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-10 relative z-10">
        <Card className="border-0 shadow-2xl bg-white/95 dark:bg-gray-800/95 backdrop-blur-md overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Enter AWB number (e.g., 00000001)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !loading) {
                      handleSearch();
                    }
                  }}
                  className="w-full h-14 pl-12 text-lg border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 rounded-xl bg-white dark:bg-gray-900"
                />
              </div>
              <Button
                onClick={() => handleSearch()}
                disabled={loading || !searchQuery.trim()}
                className="h-14 px-8 text-base font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-5 w-5" />
                    Track
                  </>
                )}
              </Button>
            </div>

            {recentAwbs.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Recent:
                </span>
                <div className="flex flex-wrap gap-2">
                  {recentAwbs.map((awb) => (
                    <button
                      key={awb}
                      type="button"
                      onClick={() => handleSearch(awb)}
                      className="px-3 py-1.5 text-xs sm:text-sm rounded-full border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:border-indigo-300 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors"
                    >
                      {awb}
                    </button>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    clearRecentAwbs(TRACKING_HISTORY_KEY);
                    setRecentAwbs([]);
                  }}
                  className="ml-auto text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // RENDER: STATUS PROGRESS BAR
  // ---------------------------------------------------------------------------

  const renderStatusProgress = () => {
    const currentStatusConfig = getStatusConfig(trackingData!.currentStatus);

    if (currentStep < 0) {
      // Special status (Returned/Cancelled)
      return (
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-center gap-4 py-4">
              <div
                className={`p-4 rounded-full ${currentStatusConfig.bgColor} ${currentStatusConfig.color}`}
              >
                {currentStatusConfig.icon}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Status</p>
                <p className={`text-2xl font-bold ${currentStatusConfig.color}`}>
                  {currentStatusConfig.label}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800 dark:to-gray-700 p-6 sm:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Tracking Number
              </p>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl sm:text-3xl font-bold font-mono text-foreground">
                  {trackingData!.awb.awbNumber}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() =>
                    copyToClipboard(trackingData!.awb.awbNumber, 'AWB Number')
                  }
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                className={`${currentStatusConfig.bgColor} ${currentStatusConfig.color} ${currentStatusConfig.borderColor} border px-4 py-2 text-sm font-medium`}
              >
                <span className="mr-2">
                  {currentStatusConfig.icon}
                </span>
                {trackingData!.currentStatus.replace('_', ' ')}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={shareTracking}
                className="hidden sm:flex"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="relative">
            {/* Progress Line Background */}
            <div className="absolute top-5 left-[calc(10%)] right-[calc(10%)] h-1 bg-gray-200 dark:bg-gray-600 rounded-full" />
            {/* Progress Line Filled */}
            <div
              className="absolute top-5 left-[calc(10%)] h-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${Math.max(0, ((currentStep - 1) / (progressSteps.length - 1)) * 80)}%`,
              }}
            />

            {/* Steps */}
            <div className="relative flex justify-between">
              {progressSteps.map((step, index) => {
                const isCompleted = currentStep > index + 1;
                const isCurrent = currentStep === index + 1;

                return (
                  <div key={step.key} className="flex flex-col items-center flex-1">
                    <div
                      className={`relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg'
                          : isCurrent
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white ring-4 ring-indigo-200 dark:ring-indigo-800 shadow-lg animate-pulse'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {step.icon}
                    </div>
                    <p
                      className={`text-xs sm:text-sm mt-3 text-center font-medium px-1 ${
                        isCompleted || isCurrent
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {step.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  // ---------------------------------------------------------------------------
  // RENDER: SHIPMENT INFO CARDS
  // ---------------------------------------------------------------------------

  const renderShipmentInfo = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Route Card */}
      <Card className="border-0 shadow-lg lg:col-span-2">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            Shipment Route
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 sm:gap-8">
            {/* Origin */}
            <div className="flex-1 p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border border-emerald-100 dark:border-emerald-800">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-800 rounded-lg">
                  <MapPin className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  Origin
                </span>
              </div>
              <p className="font-semibold text-gray-900 dark:text-white">
                {trackingData!.awb.shipper.personName ||
                  trackingData!.awb.shipper.company}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {trackingData!.awb.shipper.city},{' '}
                {trackingData!.awb.shipper.country}
              </p>
            </div>

            {/* Arrow */}
            <div className="hidden sm:flex items-center justify-center">
              <div className="flex items-center gap-2 px-4">
                <div className="w-16 h-0.5 bg-gradient-to-r from-emerald-400 to-purple-400 rounded-full" />
                <Plane className="h-6 w-6 text-purple-500 transform rotate-45" />
                <div className="w-16 h-0.5 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full" />
              </div>
            </div>

            {/* Destination */}
            <div className="flex-1 p-4 rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-100 dark:border-purple-800">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 bg-purple-100 dark:bg-purple-800 rounded-lg">
                  <MapPin className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                  Destination
                </span>
              </div>
              <p className="font-semibold text-gray-900 dark:text-white">
                {trackingData!.awb.consignee.personName ||
                  trackingData!.awb.consignee.company}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {trackingData!.awb.consignee.city},{' '}
                {trackingData!.awb.consignee.country}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Package Info Card */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            Package Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Pieces
              </span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">
              {trackingData!.awb.totalPieces}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Weight className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Weight
              </span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">
              {trackingData!.awb.totalChargeableWeight} kg
            </span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-100 dark:border-indigo-800">
            <div className="flex items-center gap-3">
              <Truck className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
              <span className="text-sm text-indigo-600 dark:text-indigo-400">
                Service
              </span>
            </div>
            <span className="font-semibold text-indigo-700 dark:text-indigo-300">
              {trackingData!.awb.service.name}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ---------------------------------------------------------------------------
  // RENDER: TIMELINE AND CONTACT INFO
  // ---------------------------------------------------------------------------

  const renderTimelineAndContacts = () => {
    const displayedEvents = isTimelineExpanded
      ? filteredEvents
      : filteredEvents.slice(0, 5);

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <Card className="border-0 shadow-lg lg:col-span-2">
          <CardHeader className="border-b dark:border-gray-700">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                Tracking History
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSearch(trackingData!.awb.awbNumber)}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
            <CardDescription>
              Complete tracking history for your shipment
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {filteredEvents.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  No tracking events available
                </p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-[7px] top-3 bottom-3 w-0.5 bg-gradient-to-b from-indigo-500 via-purple-500 to-gray-200 dark:to-gray-700" />

                {/* Events */}
                <div className="space-y-0">
                  {displayedEvents.map((event, idx) => {
                    const isFirst = idx === 0;
                    return (
                      <div key={idx} className="relative flex gap-4 pb-6">
                        {/* Timeline Dot */}
                        <div
                          className={`relative z-10 flex-shrink-0 w-4 h-4 rounded-full border-2 mt-1 ${
                            isFirst
                              ? 'bg-indigo-500 border-indigo-500 ring-4 ring-indigo-100 dark:ring-indigo-900'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                          }`}
                        />

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                            <div className="flex-1">
                              <p
                                className={`font-medium ${
                                  isFirst
                                    ? 'text-indigo-700 dark:text-indigo-400'
                                    : 'text-gray-900 dark:text-white'
                                }`}
                              >
                                {event.eventDescription}
                              </p>
                              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
                                <MapPin className="h-3 w-3" />
                                <span>{event.eventLocation}</span>
                              </div>
                              {event.remarks && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 italic">
                                  "{event.remarks}"
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  event.eventType === 'API'
                                    ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
                                    : 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
                                }`}
                              >
                                {event.eventType}
                              </Badge>
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {formatDateTime(event.eventDate, event.eventTime)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Show More Button */}
                {filteredEvents.length > 5 && (
                  <button
                    onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
                    className="w-full text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline text-center pt-2 font-medium focus:outline-none"
                  >
                    {isTimelineExpanded
                      ? 'Show less'
                      : `+${filteredEvents.length - 5} more events`}
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Cards */}
        <div className="space-y-6">
          {/* Shipper Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <User className="h-4 w-4" />
                Shipper
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-semibold text-gray-900 dark:text-white">
                {trackingData!.awb.shipper.personName ||
                  trackingData!.awb.shipper.company}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {trackingData!.awb.shipper.city},{' '}
                {trackingData!.awb.shipper.country}
              </p>
              {trackingData!.awb.shipper.phoneNumber && (
                <button
                  onClick={() =>
                    copyToClipboard(
                      trackingData!.awb.shipper.phoneNumber,
                      'Phone number'
                    )
                  }
                  className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  <Phone className="h-4 w-4" />
                  {trackingData!.awb.shipper.phoneNumber}
                </button>
              )}
            </CardContent>
          </Card>

          {/* Consignee Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <User className="h-4 w-4" />
                Consignee
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-semibold text-gray-900 dark:text-white">
                {trackingData!.awb.consignee.personName ||
                  trackingData!.awb.consignee.company}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {trackingData!.awb.consignee.city},{' '}
                {trackingData!.awb.consignee.country}
              </p>
              {trackingData!.awb.consignee.phoneNumber && (
                <button
                  onClick={() =>
                    copyToClipboard(
                      trackingData!.awb.consignee.phoneNumber,
                      'Phone number'
                    )
                  }
                  className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  <Phone className="h-4 w-4" />
                  {trackingData!.awb.consignee.phoneNumber}
                </button>
              )}
            </CardContent>
          </Card>

          {/* Estimated Delivery */}
          {trackingData!.estimatedDelivery && (
            <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-100 dark:border-amber-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    Estimated Delivery
                  </span>
                </div>
                <p className="text-xl font-bold text-amber-800 dark:text-amber-200">
                  {formatDate(trackingData!.estimatedDelivery)}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // RENDER: HELP SECTION
  // ---------------------------------------------------------------------------

  const renderHelpSection = () => (
    <Card className="border-0 shadow-lg bg-gradient-to-r from-slate-800 to-gray-900 dark:from-slate-900 dark:to-gray-950 text-white overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
              <HelpCircle className="h-8 w-8" />
            </div>
            <div>
              <h3 className="font-semibold text-xl mb-1">Need Help?</h3>
              <p className="text-gray-300 text-sm sm:text-base">
                Can't find your shipment? Our support team is here to help.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            >
              <Phone className="h-4 w-4 mr-2" />
              Call Support
            </Button>
            <Button
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Us
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // ---------------------------------------------------------------------------
  // MAIN RENDER
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Search Section with Hero */}
      {renderSearchSection()}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Error Display */}
        {error && (
          <Alert
            className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
            variant="destructive"
          >
            <AlertDescription className="text-red-800 dark:text-red-300">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Loading Skeleton */}
        {loading && !trackingData && (
          <div className="space-y-6">
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-6 sm:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-8 w-56" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-9 w-32" />
                    <Skeleton className="h-9 w-24 hidden sm:block" />
                  </div>
                </div>
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="border-0 shadow-lg lg:col-span-2">
                <CardHeader className="pb-4">
                  <Skeleton className="h-5 w-40" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-4">
                  <Skeleton className="h-5 w-40" />
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-lg">
              <CardHeader className="border-b dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-5 w-44" />
                  <Skeleton className="h-9 w-28" />
                </div>
                <Skeleton className="h-4 w-64 mt-2" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {!loading && !trackingData && !error && !hasSearched && (
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-8 sm:p-10">
              <div className="max-w-2xl mx-auto text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 mb-4">
                  <Search className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  Enter an AWB number to start tracking
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Paste your tracking number above and hit <span className="font-medium">Track</span> to see the latest status and full history.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tracking Results */}
        {trackingData && (
          <div className="space-y-6">
            {renderStatusProgress()}
            {renderShipmentInfo()}
            {renderTimelineAndContacts()}
          </div>
        )}

        {/* Help Section */}
        {renderHelpSection()}
      </div>
    </div>
  );
};

export default TrackingPage;
